import { useState } from 'react'
import { router } from '@inertiajs/react'
import {
  EditOutlined,
  DownloadOutlined,
  ArrowLeftOutlined,
  UserAddOutlined,
  DeleteOutlined,
} from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import EventLayout from '@/components/layouts/EventLayout'
import { Button, IconButton, Table, TableActions, Breadcrumb, ConfirmModal, message } from '@/components/ui'
import ScanPointDrawer from '@/components/scan_points/ScanPointDrawer'
import AddAttendeesModal from '@/components/scan_points/AddAttendeesModal'

interface Event {
  id: number
  name: string
  slug: string
  start_date: string
  end_date: string
}

interface ScanPoint {
  id: number
  name: string
  kind: string
  start_at: string | null
  end_at: string | null
  allow_anytime_scans: boolean
  allow_multiple_scans: boolean
  agenda_session_id: number | null
  agenda_session_name: string | null
  scan_count: number
}

interface ScannedAttendee {
  id: number
  registrant_id: number
  full_name: string
  email: string
  contact_number: string | null
  scanned_at: string
}

interface ApprovedRegistrant {
  id: number
  full_name: string
  email: string
  contact_number: string | null
  answers?: Record<string, any>
}

interface FilterFieldOption {
  id: number
  label: string
  value: string
}

interface FilterFieldGroup {
  fieldId: number
  label: string
  identifier: string
  options: FilterFieldOption[]
}

interface FilterOptions {
  fields: FilterFieldGroup[]
}

interface Pagination {
  current_page: number
  total_pages: number
  total_count: number
  per_page: number
}

interface AttendeesProps {
  event: Event
  scan_point: ScanPoint
  scanned_attendees: ScannedAttendee[]
  scanned_registrant_ids: number[]
  approved_registrants: ApprovedRegistrant[]
  filter_options: FilterOptions
  pagination: Pagination
}

export default function Attendees({
  event,
  scan_point,
  scanned_attendees,
  scanned_registrant_ids,
  approved_registrants,
  filter_options,
  pagination,
}: AttendeesProps) {
  const [editDrawerVisible, setEditDrawerVisible] = useState(false)
  const [addModalVisible, setAddModalVisible] = useState(false)
  const [addSubmitting, setAddSubmitting] = useState(false)
  const [attendeeToDelete, setAttendeeToDelete] = useState<ScannedAttendee | null>(null)
  const [deleteSubmitting, setDeleteSubmitting] = useState(false)

  const handleBack = () => {
    router.visit(`/events/${event.slug}/scan_points`)
  }

  const handleEdit = () => {
    setEditDrawerVisible(true)
  }

  const handleExport = () => {
    window.location.href = `/events/${event.slug}/scan_points/${scan_point.id}/attendees.csv`
  }

  const handleOpenAddModal = () => {
    setAddModalVisible(true)
  }

  const handlePaginationChange = (page: number, pageSize: number) => {
    const nextPerPage = pageSize || pagination.per_page
    const nextPage = nextPerPage !== pagination.per_page ? 1 : page

    router.get(
      `/events/${event.slug}/scan_points/${scan_point.id}/attendees`,
      {
        page: nextPage,
        per_page: nextPerPage,
      },
      {
        preserveState: true,
        preserveScroll: true,
        replace: true,
      }
    )
  }

  const handleDeleteClick = (attendee: ScannedAttendee) => {
    setAttendeeToDelete(attendee)
  }

  // The server redirects back to the attendees page, so the current view state
  // travels in the query string to survive the round trip.
  const currentViewQuery = () =>
    new URLSearchParams({
      page: String(pagination.current_page),
      per_page: String(pagination.per_page),
    }).toString()

  const handleDelete = () => {
    if (!attendeeToDelete) return

    setDeleteSubmitting(true)
    router.delete(
      `/events/${event.slug}/scan_points/${scan_point.id}/attendees/${attendeeToDelete.id}?${currentViewQuery()}`,
      {
        // Both outcomes come back as a redirect carrying flash[:notice] or
        // flash[:alert], which EventLayout's FlashMessages already surfaces —
        // a toast from here would either duplicate or contradict it.
        preserveScroll: true,
        onFinish: () => {
          setDeleteSubmitting(false)
          setAttendeeToDelete(null)
        },
      }
    )
  }

  const handleAddAttendees = (registrantIds: number[]) => {
    setAddSubmitting(true)
    router.post(
      `/events/${event.slug}/scan_points/${scan_point.id}/add_attendees`,
      { attendee_ids: registrantIds },
      {
        onSuccess: () => {
          setAddSubmitting(false)
          setAddModalVisible(false)
          message.success('Attendees added successfully')
        },
        onError: () => {
          setAddSubmitting(false)
          message.error('Failed to add attendees')
        },
      }
    )
  }

  const handleUpdateScanPoint = (values: any) => {
    router.patch(`/events/${event.slug}/scan_points/${scan_point.id}`, {
      scan_point: values
    }, {
      onSuccess: () => {
        setEditDrawerVisible(false)
        router.reload()
      }
    })
  }

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }) + ', ' + date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    })
  }

  const columns: ColumnsType<ScannedAttendee> = [
    {
      title: 'Full Name',
      dataIndex: 'full_name',
      key: 'full_name',
      sorter: true,
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      sorter: true,
    },
    {
      title: 'Contact Number',
      dataIndex: 'contact_number',
      key: 'contact_number',
      sorter: true,
      render: (contact: string | null) => contact || '-',
    },
    {
      title: 'Scanned At',
      dataIndex: 'scanned_at',
      key: 'scanned_at',
      sorter: true,
      render: (date: string) => formatDateTime(date),
    },
    {
      title: 'Action',
      key: 'action',
      width: 100,
      render: (_: unknown, attendee) => (
        <TableActions
          actions={[
            {
              key: 'delete',
              icon: <DeleteOutlined />,
              onClick: () => handleDeleteClick(attendee),
              ariaLabel: `Delete ${attendee.full_name}`,
              title: 'Delete',
              danger: true,
            },
          ]}
        />
      ),
    },
  ]

  return (
    <EventLayout>
      <div>
        {/* Breadcrumb */}
        <div className="px-6 py-3">
          <Breadcrumb
            items={[
              {
                title: <a onClick={handleBack}>Scan Points</a>,
              },
              {
                title: 'View Attendees',
              },
            ]}
          />
        </div>

        {/* Header */}
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <IconButton
                icon={<ArrowLeftOutlined />}
                onClick={handleBack}
                ariaLabel="Go back to Scan Points"
                title="Back"
              />
              <h1 className="text-xl font-semibold text-gray-900">{scan_point.name}</h1>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="primary"
                icon={<UserAddOutlined />}
                onClick={handleOpenAddModal}
              >
                Add Attendees
              </Button>
              <Button
                variant="secondary"
                icon={<EditOutlined />}
                onClick={handleEdit}
              >
                Edit
              </Button>
              <Button
                variant="secondary"
                icon={<DownloadOutlined />}
                onClick={handleExport}
              >
                Export Report
              </Button>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="p-6">
          <Table
            columns={columns}
            dataSource={scanned_attendees}
            rowKey="id"
            className="ava-data-table"
            showSorterTooltip={false}
            pagination={{
              current: pagination.current_page,
              total: pagination.total_count,
              pageSize: pagination.per_page,
              showTotal: (total) => `Total ${total} items`,
              showSizeChanger: true,
              pageSizeOptions: ['10', '25', '50', '100'],
              onChange: handlePaginationChange,
            }}
          />
        </div>
      </div>

      {/* Edit Drawer */}
      <ScanPointDrawer
        visible={editDrawerVisible}
        onClose={() => setEditDrawerVisible(false)}
        onSubmit={handleUpdateScanPoint}
        editingScanPoint={scan_point}
        agendaSessions={[]}
        eventStartDate={event.start_date}
        eventEndDate={event.end_date}
      />

      <AddAttendeesModal
        open={addModalVisible}
        onClose={() => setAddModalVisible(false)}
        onAdd={handleAddAttendees}
        registrants={approved_registrants}
        existingRegistrantIds={scanned_registrant_ids}
        filterOptions={filter_options}
        loading={addSubmitting}
      />

      <ConfirmModal
        title="Delete Attendee"
        open={attendeeToDelete !== null}
        onConfirm={handleDelete}
        onClose={() => setAttendeeToDelete(null)}
        confirmText="Delete"
        loading={deleteSubmitting}
        danger
      >
        <p className="text-sm text-gray-600">
          Are you sure you want to delete{' '}
          <span className="font-medium text-gray-900">{attendeeToDelete?.full_name}</span> from this
          scan point? This permanently removes their scan record and cannot be undone.
        </p>
      </ConfirmModal>
    </EventLayout>
  )
}
